export interface ClientErrorPayload {
  message: string;
  stack: string;
  source: string;
  path: string;
  metadata: Record<string, unknown>;
}

interface ErrorContext {
  source?: string;
  metadata?: Record<string, unknown>;
}

declare global {
  interface WindowEventMap {
    "cleverfleet:client-error": CustomEvent<{ message?: string; source?: string }>;
  }

  // eslint-disable-next-line @typescript-eslint/no-namespace
  var cleverfleetMonitoringProvider:
    | { captureException?: (error: unknown, payload: ClientErrorPayload) => void }
    | undefined;
}

const reportedFingerprints = new Map<string, number>();
const DEDUPLICATION_WINDOW_MS = 30_000;

export function normalizeClientError(error: unknown, context: ErrorContext = {}): ClientErrorPayload {
  const value =
    error instanceof Error ? error : new Error(typeof error === "string" ? error : "Unbekannter Browserfehler");
  return {
    message: value.message.slice(0, 1000),
    stack: String(value.stack || "").slice(0, 5000),
    source: String(context.source || "browser").slice(0, 100),
    path: typeof location === "undefined" ? "" : `${location.pathname}${location.search}`.slice(0, 500),
    metadata: context.metadata || {}
  };
}

function shouldReport(payload: ClientErrorPayload): boolean {
  const fingerprint = `${payload.source}:${payload.message}:${payload.stack.split("\n")[1] || ""}`;
  const now = Date.now();
  const lastReported = reportedFingerprints.get(fingerprint) || 0;
  reportedFingerprints.set(fingerprint, now);
  for (const [key, timestamp] of reportedFingerprints) {
    if (now - timestamp > DEDUPLICATION_WINDOW_MS) reportedFingerprints.delete(key);
  }
  return now - lastReported > DEDUPLICATION_WINDOW_MS;
}

export function reportClientError(error: unknown, context: ErrorContext = {}): void {
  const payload = normalizeClientError(error, context);
  if (!shouldReport(payload)) return;

  try {
    globalThis.cleverfleetMonitoringProvider?.captureException?.(error, payload);
  } catch (providerError) {
    console.warn("Externer Monitoring-Provider ist fehlgeschlagen.", providerError);
  }

  const body = JSON.stringify(payload);
  if (navigator.sendBeacon) {
    navigator.sendBeacon("/api/client-errors", new Blob([body], { type: "application/json" }));
    return;
  }
  fetch("/api/client-errors", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    credentials: "same-origin",
    keepalive: true
  }).catch(() => {});
}

export function installClientMonitoring(windowRef: Window & typeof globalThis = window): void {
  const installed = windowRef as typeof windowRef & { __cleverfleetMonitoringInstalled?: boolean };
  if (installed.__cleverfleetMonitoringInstalled) return;
  installed.__cleverfleetMonitoringInstalled = true;

  windowRef.addEventListener("error", (event) => {
    reportClientError(event.error || event.message, { source: "window.error" });
  });
  windowRef.addEventListener("unhandledrejection", (event) => {
    reportClientError(event.reason, { source: "unhandledrejection" });
  });
  windowRef.addEventListener("cleverfleet:client-error", (event) => {
    reportClientError(event.detail?.message, { source: event.detail?.source || "client" });
  });
}
